/*
 * Copyright (C) 2018 Spanning Cloud Apps.  All rights reserved.
 */
import $ from 'jquery';

// Third-party modules (use the npm package name)
import angular from 'angular';
import 'angular-animate';
import 'angular-cookies';
import 'angular-credit-cards';
import 'angular-sanitize';
import 'angular-ui-bootstrap';
import 'angular-ui-router';
import 'angular-ui-utils/modules/format/format';
import 'ng-file-upload';
import 'angular-recursion';

// Set up Font Awesome glyphs
import { library as faLib, dom as faDom } from '@fortawesome/fontawesome-svg-core';
import { faCaretLeft } from '@fortawesome/free-solid-svg-icons/faCaretLeft';
import { faCaretRight } from '@fortawesome/free-solid-svg-icons/faCaretRight';
import { faCircle } from '@fortawesome/free-solid-svg-icons/faCircle';
import { faEdit } from '@fortawesome/free-solid-svg-icons/faEdit';
import { faSave } from '@fortawesome/free-solid-svg-icons/faSave';
import { faTimes } from '@fortawesome/free-solid-svg-icons/faTimes';
import { faUndo } from '@fortawesome/free-solid-svg-icons/faUndo'; // Allow automatic replacement of icons

import { initHeatmap } from '../common/utils/heatmap';
import { initWalkMe } from '../common/utils/walkMe';

// Spanning modules and templates (use the relative path to the controller file)
import '../common/constants/constants.js';
import '../common/constants/enums.js';
import '../common/directives/directives.js';
import '../common/filters/filters.js';
import '../common/http/http.js';
import '../common/interceptors/interceptors.js';
import '../common/services/services.js';
import '../common/stores/stores.js';
import 'imports-loader?this=>window!../lib/grunticon/grunticon.js';
import '../msg/sbsf_resource.js';
import './account/account.js';
import './admin/admin.js';
import './batch/batch.js';
import './bulkRestore/restore.js';
import './bulkExport/export.js';
import './gdprRequest/gdprRequest.js';
import './chatter/chatter.js';
import './log/log.js';
import './metadataCompare/metadataCompare.js';
import './mobile/mobile.js';
import './receipts/receipts.js';
import './restore/confirm/confirm.js';
//import './restoreNew/restoreNew.js';
import './shared/shared.js';

// Styles
import '../styles/main.scss';
import permissions from '../common/constants/permissions';

window.jQuery = $;
window.$ = $;

faLib.add(faCaretLeft);
faLib.add(faCaretRight);
faLib.add(faCircle);
faLib.add(faEdit);
faLib.add(faSave);
faLib.add(faTimes);
faLib.add(faUndo);
faDom.watch();

const dependencies = [
  // Third-party dependencies
  'credit-cards',
  'ngAnimate',
  'ngCookies',
  'ngFileUpload',
  'ngSanitize',
  'ui.bootstrap',
  'ui.format',
  'ui.router',
  'RecursionHelper',
  // Spanning dependencies
  'account',
  'admin',
  'batch',
  'bulkRestore',
  'bulkExport',
  'gdprRequest',
  'chatter',
  'log',
  'metadataCompare',
  'mobile',
  'receipts',
  //'restoreReact',
  'sbsf.constants',
  'sbsf.directives',
  'sbsf.filters',
  'sbsf.http',
  'sbsf.interceptors',
  'sbsf.services',
  'sbsf.shared',
  'sbsf.stores'
];

const MODULE_NAME = 'SBSF';

const app = angular.module(MODULE_NAME, dependencies).config(config).run(run);

/**
 * The configuration function for the module.
 */
function config($httpProvider, $uibTooltipProvider) {
  // Do not configure urlRouting b/c we have to process params set by the visual force page on the
  // signed request in order to determine the route for the request.

  // Configure the default header specifying the backup app
  $httpProvider.defaults.headers.common.App = 'sb4sf';

  $uibTooltipProvider.options({ popupDelay: 1000 /*msec*/ });
}

/**
 * The run function for the module.
 */
function run(
  $filter,
  $rootScope,
  $state,
  $location,
  $stateParams,
  authFactory,
  batchUtils,
  configService,
  httpAccount,
  httpBatch,
  permissionService,
  progressFactory,
  UserAgentFactory,
  ALERT_EVENTS,
  BATCH_RESULTS,
  VIEW_STATES,
  IntercomFactory
) {
  progressFactory.loaded = true;

  // Tie in progressFactory properties to rootScope so they are available app wide.
  $rootScope.backupInProgress = progressFactory.backupInProgress;
  $rootScope.exportInProgress = progressFactory.exportInProgress;
  $rootScope.bulkRestoreInProgress = progressFactory.bulkRestoreInProgress;
  $rootScope.bulkRestorePreviewInProgress = progressFactory.bulkRestorePreviewInProgress;
  $rootScope.bulkRestoreCsvVerificationInProgress = progressFactory.bulkRestoreCsvVerificationInProgress;
  $rootScope.metadataCompareInProgress = progressFactory.metadataCompareInProgress;
  $rootScope.backupProgress = progressFactory.backup;
  $rootScope.exportProgress = progressFactory.exportObj;
  $rootScope.bulkRestoreProgress = progressFactory.bulkRestoreObj;
  $rootScope.bulkRestorePreviewProgress = progressFactory.bulkRestorePreviewObj;
  $rootScope.bulkRestoreCsvVerificationProgress = progressFactory.bulkRestoreCsvVerificationObj;
  $rootScope.metadataCompareProgress = progressFactory.metadataCompareObj;
  $rootScope.metadataRestoreProgress = progressFactory.metadataRestoreObj;
  $rootScope.gdprRequestProgress = progressFactory.gdprOperationObj;
  // The flag for if we are in maintenance mode.
  $rootScope.maintenanceModeFlag = false;
  // Flag if there is an accessError
  $rootScope.accessError = false;

  // for downloading activity history csv and audit history csv
  $rootScope.activityHistoryDownloadInProgress = false;
  $rootScope.auditHistoryDownloadInProgress = false;

  $rootScope.isUserLoading = true;

  progressFactory.initProgressWatches();

  $rootScope.$on('$viewContentLoaded', function () {
    window.scrollTo(0, 0);
  });

  $rootScope.$watch('exportInProgress', (newValue, oldValue) => {
    if (newValue === false && oldValue === true) {
      httpBatch.getLatestExport().then((batch) => {
        const onClickFunction = () => {
          batchUtils.currentBatch = batch;
          $state.go('batchMain.export', { batchId: batch.id });
        };

        if (batch.status === BATCH_RESULTS.CANCELLED) {
          $rootScope.$emit(ALERT_EVENTS.SUCCESS.eventName, $filter('i18n')('EXPORT_CANCELLED'), false, onClickFunction);
        } else {
          $rootScope.$emit(ALERT_EVENTS.SUCCESS.eventName, $filter('i18n')('EXPORT_COMPLETED'), false, onClickFunction);
        }
      });
    }
  });

  $rootScope.$watch('bulkRestorePreviewInProgress', (newValue, oldValue) => {
    if (newValue === false && oldValue === true) {
      httpBatch.getLatestBulkRestorePreview().then((batch) => {
        const onClickFunction = () => {
          batchUtils.currentBatch = batch;
          $state.go('batchMain.export', { batchId: batch.id });
        };
        $rootScope.$emit(
          ALERT_EVENTS.SUCCESS.eventName,
          $filter('i18n')('BULK_RESTORE_PREVIEW_COMPLETED'),
          false,
          onClickFunction
        );
      });
    }
  });

  const intercomStateHandler = (toState, fromState) => {
    if (toState.name === VIEW_STATES.ADMIN_HOME && fromState.name !== '') {
      IntercomFactory.show();
    }
    if (toState.name !== VIEW_STATES.ADMIN_HOME && fromState.name === VIEW_STATES.ADMIN_HOME) {
      IntercomFactory.hide();
    }
  };

  // Listen for the state change events and alter the root level
  // loading mask trigger.
  //eslint-disable-next-line no-unused-vars
  $rootScope.$on('$stateChangeStart', (event, toState, toParams, fromState) => {
    // console.log(`$stateChangeStart from ${fromState.name} to ${toState.name}`);
    $rootScope.showLoadingMask = true;
  });

  //eslint-disable-next-line no-unused-vars
  $rootScope.$on('$stateChangeSuccess', (event, toState, toParams, fromState) => {
    // console.log(`$stateChangeSuccess from ${fromState.name} to ${toState.name}`);
    $rootScope.showLoadingMask = false;
    intercomStateHandler(toState, fromState);
  });

  //eslint-disable-next-line no-unused-vars
  $rootScope.$on('stateChangeStopped', (event, toState, toParams, fromState) => {
    // console.log(`$stateChangeStopped from ${fromState.name} to ${toState.name}`);
    $rootScope.showLoadingMask = false;
  });

  //eslint-disable-next-line no-unused-vars
  $rootScope.$on('$stateNotFound', (event, unfoundState, fromState) => {
    // console.log(`$stateNotFound: ${unfoundState.name} from ${fromState.name}`);
    $rootScope.showLoadingMask = false;
  });

  //eslint-disable-next-line no-unused-vars
  $rootScope.$on('$stateChangeError', (event, toState, toParams, fromState, fromParams, error) => {
    // console.log(`$stateChangeError from ${fromState.name} to ${toState.name}: ${error}`);
    $rootScope.showLoadingMask = false;
  });

  $rootScope.$on(ALERT_EVENTS.UNAUTHORIZED.eventName, (event) => {
    configService.getConfig().then((config) => {
      window.open(config.broker.logoutUrl, '_self');
    });
  });

  $rootScope.$on(ALERT_EVENTS.EXPIRED_ORG.eventName, () => {
    configService.getConfig().then((config) => {
      window.open(config.broker.expirationUrl, '_self');
    });
  });

  configService.getConfig().then((config) => {
    if (config.heatmapAppId) {
      initHeatmap(config.heatmapAppId);
    }
  });

  Promise.all([configService.getConfig(), authFactory.getSignedRequest()]).then(([config, account]) => {
    if (account.walkMe && config.walkMeUrl) {
      initWalkMe(account.walkMe, config.walkMeUrl);
    }
  });

  $rootScope.state = $state;
  $rootScope.stateParams = $stateParams;

  // Watch the maintenance mode flag for changes. If it changes to
  // true then go to maintenance mode state.
  $rootScope.$watch('maintenanceModeFlag', (newValue) => {
    if (newValue) {
      $state.go('maintenanceMode');
    }
  });

  // object to hold the route keys and states
  const routes = {
    account: VIEW_STATES.ACCOUNT,
    alertEventChatter: VIEW_STATES.CHATTER_ALERT_EVENT,
    apiChange: VIEW_STATES.CHATTER_API,
    backup: VIEW_STATES.CHATTER_BACKUP,
    batch: VIEW_STATES.BATCH_HOME,
    bulkRestoreChatter: VIEW_STATES.CHATTER_BULK_RESTORE,
    bulkRestoreCsvVerificationChatter: VIEW_STATES.CHATTER_BULK_RESTORE_CSV_VERIFICATION,
    bulkRestorePreviewChatter: VIEW_STATES.CHATTER_EXPORT,
    bulkRestoreDetails: VIEW_STATES.BATCH_BULK_RESTORE,
    metadataCompareDetails: VIEW_STATES.METADATA_COMPARE_RESULTS,
    metadataCompareChatter: VIEW_STATES.CHATTER_METADATA_COMPARE,
    metadataRestoreDetails: VIEW_STATES.BATCH_METADATA_RESTORE,
    metadataRestoreChatter: VIEW_STATES.CHATTER_METADATA_RESTORE,
    main: VIEW_STATES.ADMIN_HOME,
    mobile: VIEW_STATES.MOBILE_HOME,
    mobileSupport: VIEW_STATES.MOBILE_SUPPORT_HOME,
    exportDetails: VIEW_STATES.BATCH_EXPORT,
    export: VIEW_STATES.CHATTER_EXPORT,
    log: VIEW_STATES.LOG_HOME,
    bulkRestore: VIEW_STATES.BULK_RESTORE_ENTER,
    bulkRestoreCsv: VIEW_STATES.BULK_RESTORE_ENTER,
    restore: VIEW_STATES.BATCH_OBJECT_RESTORE,
    //restoreNew: VIEW_STATES.RESTORE_REACT,
    bulkExport: VIEW_STATES.BULK_EXPORT_ENTER,
    gdprRequest: VIEW_STATES.GDPR_REQUEST_ENTER,
    bulkExportCsv: VIEW_STATES.BULK_EXPORT_ENTER
  };

  const mobileRoutes = {
    apiChange: 'chatterAPI',
    backup: 'mobileBatchMain.batch',
    export: 'mobileBatchMain.export',
    bulkRestoreChatter: 'mobileBatchMain.bulkRestore',
    bulkRestoreCsvVerificationChatter: 'mobileBatchMain.bulkRestore',
    bulkRestorePreviewChatter: 'mobileBatchMain.export'
  };

  // Read the parameters property set on the signed request and route accordingly.
  authFactory
    .getSignedRequest()
    .then((userInfo) => {
      if (!userInfo.context) {
        throw new Error('invalidConfig');
      } else {
        const stateAndParams = prepareStateToGo(userInfo.context.environment.parameters);
        $rootScope.instanceUrl = userInfo.client.instanceUrl;
        // If on a mobile device route to mobile view and add mobile body cls.
        if (UserAgentFactory.detectMobileDevice()) {
          $rootScope.inMobile = true;
          $rootScope.bodyCls = 'sbsf-sf1';
        }

        if (stateAndParams.state) {
          $state.go(stateAndParams.state, stateAndParams.params);
        }

        // As we load the state, see if we will need to display a maintenance
        // message to the user. This will only occur on initial load of the app.
        httpAccount.getMaintenanceNotification().then((obj) => {
          // Make sure it's not an error code that we've intercepted.
          if (obj.message && !obj.code) {
            $rootScope.$emit(ALERT_EVENTS.SUCCESS.eventName, obj.message, true, null);
          }
        });

        permissionService.hasPermission(permissions.IT_COMPLETE_FEATURE).then((hasPermission) => {
          userInfo.isAdmin = hasPermission;
          window.initReactContainers(userInfo, userInfo.organization);
        });

        $rootScope.isUserLoading = false;
      }
    })
    .catch(() => {
      $rootScope.accessError = true;
      $state.go('invalidConfig');
    });

  /**
   * Prepare routing state params using url and context
   */
  function prepareStateToGo(contextParams) {
    let stateAndParams = {
      state: null,
      params: {}
    };
    const inMobile = UserAgentFactory.detectMobileDevice();
    $rootScope.inMobile = inMobile;
    $rootScope.isAsideMenuOpen = !inMobile;

    if (contextParams.page) {
      if (routes[contextParams.page]) {
        stateAndParams.state = routes[contextParams.page];
        stateAndParams.params = contextParams;
      } else if (inMobile && mobileRoutes[contextParams.page]) {
        stateAndParams.state = mobileRoutes[contextParams.page];
        stateAndParams.params = contextParams;
      }
    }
    const urlParts = $location
      .path()
      .split('/')
      .filter((part) => part);
    if (urlParts.length) {
      if (!inMobile && !isRouteExist(routes, urlParts[0])) {
        stateAndParams.state = routes.main;
      } else if (inMobile && !isRouteExist(mobileRoutes, urlParts[0])) {
        stateAndParams.state = routes.mobile;
      }
    } else {
      stateAndParams.state = !inMobile ? routes.main : routes.mobile;
    }

    return stateAndParams;
  }

  /**
   * Check if route string exists case insensitively
   */
  function isRouteExist(routes, value) {
    for (const route in routes) {
      if (routes.hasOwnProperty(route) && route.toLowerCase() === value.toLowerCase()) {
        return true;
      }
    }
    return false;
  }
}

window.angular_app = app;

export default MODULE_NAME;
